/**
 * My submissions — what happened to the work after it left the device.
 *
 * The phone had nothing to say about the second half of a session's life.
 * Submitting moved a row out of the hub's working list and that was the end
 * of it: no confirmation it had landed, no sign a reviewer had picked it up,
 * no way to learn it had been rejected. The sync layer was already pulling
 * `/sessions/mine/reviewed` into SQLite on every cycle — the outcome was on
 * the device the whole time, with no screen reading it.
 *
 * Everything here is a column: `status` for the stage, `submitted_at` and
 * `approved_at` for the timestamps, and the sync queue for whether the
 * device still owes the server a write. What the design prototype puts on
 * Two of the things the design prototype put here now exist, and one still
 * does not.
 *
 * The reviewer's note travels back on `sessions.review_note`, and
 * `changes_requested` is the non-terminal decline that makes "fix it"
 * possible: it is in `INSPECTOR_WRITABLE_STATUSES`, so the edit the note asks
 * for is allowed, and submitting again is the transition out of it. `rejected`
 * stays terminal and gets no such button — offering one there would 403,
 * which is the thing this comment used to refuse to ship.
 *
 * Still missing: the note cannot name *which* fields were flagged. That needs
 * a per-field structure the note column does not have, so a reviewer says it
 * in prose and the inspector reads it in prose.
 */
import type { ReactNode } from 'react';
import { useMemo, useState } from 'react';
import { createFileRoute, Link } from '@tanstack/react-router';
import { Chip, GestureBar, I, Ico, ScreenFrame, StatusBar, T } from '@/components/primitives';
import { useMySubmissions, useSyncStatus } from '@kapture/client-core';
import type { SubmissionRow } from '@kapture/domain';

export const Route = createFileRoute('/submissions')({
  component: MySubmissionsPage,
  staticData: { mobile: true },
});

type Tab = 'all' | 'waiting' | 'approved' | 'sent_back';

const TABS: { id: Tab; label: string }[] = [
  { id: 'all', label: 'All' },
  { id: 'waiting', label: 'Waiting' },
  { id: 'approved', label: 'Approved' },
  { id: 'sent_back', label: 'Sent back' },
];

function matches(tab: Tab, status: SubmissionRow['status']): boolean {
  if (tab === 'all') return true;
  if (tab === 'waiting') return status === 'submitted' || status === 'in_review';
  if (tab === 'approved') return status === 'approved';
  return status === 'rejected' || status === 'changes_requested';
}

/**
 * The readable part of a user id. There is no profile lookup on the device —
 * `reviewer_id` is whatever the server sent — so this trims the domain rather
 * than inventing a display name out of it.
 */
function personLabel(id: string): string {
  return id.includes('@') ? id.slice(0, id.indexOf('@')) : id;
}

function timeAgo(iso: string | null): string {
  if (!iso) return 'not sent yet';
  const diffMin = Math.round((Date.now() - new Date(iso).getTime()) / 60_000);
  if (diffMin < 1) return 'just now';
  if (diffMin < 60) return `${diffMin}m ago`;
  const diffH = Math.round(diffMin / 60);
  if (diffH < 24) return `${diffH}h ago`;
  return `${Math.round(diffH / 24)}d ago`;
}

function MySubmissionsPage() {
  const { data: submissions, isLoading } = useMySubmissions();
  const { online, pending } = useSyncStatus();
  const [tab, setTab] = useState<Tab>('all');

  const list = useMemo(() => submissions ?? [], [submissions]);
  const rows = useMemo(() => list.filter((s) => matches(tab, s.status)), [list, tab]);

  const counts = useMemo(
    () => ({
      waiting: list.filter((s) => s.status === 'submitted' || s.status === 'in_review').length,
      approved: list.filter((s) => s.status === 'approved').length,
      sentBack: list.filter(
        (s) => s.status === 'rejected' || s.status === 'changes_requested',
      ).length,
    }),
    [list],
  );

  return (
    <ScreenFrame tone="light">
      <StatusBar />
      <div
        style={{
          padding: '10px 16px 12px',
          borderBottom: `1px solid ${T.stroke}`,
          background: T.surface,
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <Link
            to="/"
            aria-label="Back"
            style={{ color: T.text, display: 'flex', textDecoration: 'none' }}
          >
            <Ico d={I.back} size={20} />
          </Link>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div
              style={{
                fontSize: 17,
                fontWeight: 800,
                fontFamily: T.font,
                letterSpacing: '-0.02em',
              }}
            >
              My submissions
            </div>
            <div style={{ fontSize: 10, color: T.textMute, fontFamily: T.mono, marginTop: 1 }}>
              {list.length} sent · {counts.waiting} waiting on a reviewer
            </div>
          </div>
          <Chip tone={!online ? 'warn' : pending > 0 ? 'info' : 'good'} size="sm">
            <span
              style={{
                width: 5,
                height: 5,
                borderRadius: 3,
                background: !online ? T.warn : pending > 0 ? T.info : T.good,
                display: 'inline-block',
              }}
            />
            {!online ? 'offline' : pending > 0 ? `${pending} to send` : 'synced'}
          </Chip>
        </div>

        <div
          style={{
            display: 'flex',
            gap: 4,
            marginTop: 11,
            background: T.surface3,
            borderRadius: 10,
            padding: 3,
          }}
        >
          {TABS.map((t) => {
            const active = tab === t.id;
            return (
              <button
                key={t.id}
                type="button"
                onClick={() => setTab(t.id)}
                style={{
                  flex: 1,
                  border: 'none',
                  cursor: 'pointer',
                  padding: '6px 4px',
                  borderRadius: 8,
                  background: active ? T.surface : 'transparent',
                  color: active ? T.text : T.textDim,
                  fontFamily: T.font,
                  fontSize: 11.5,
                  fontWeight: active ? 700 : 600,
                  boxShadow: active ? T.sh : 'none',
                }}
              >
                {t.label}
              </button>
            );
          })}
        </div>
      </div>

      <div style={{ flex: 1, overflow: 'auto', padding: 14 }}>
        {isLoading && (
          <div
            style={{
              padding: 32,
              textAlign: 'center',
              color: T.textMute,
              fontSize: 13,
              fontFamily: T.font,
            }}
          >
            Loading submissions…
          </div>
        )}
        {!isLoading && rows.length === 0 && (
          <EmptyFeed tab={tab} anySubmissions={list.length > 0} counts={counts} />
        )}
        {rows.map((s) => (
          <SubmissionCard key={s.id} submission={s} />
        ))}
      </div>
      <GestureBar />
    </ScreenFrame>
  );
}

/* ----------------------------------------------------------------- card -- */

interface StatusLook {
  label: string;
  tone: 'info' | 'warn' | 'good' | 'danger';
  dot: string;
}

const STATUS_LOOK: Record<string, StatusLook> = {
  submitted: { label: 'Submitted', tone: 'info', dot: T.info },
  in_review: { label: 'In review', tone: 'warn', dot: T.warn },
  approved: { label: 'Approved', tone: 'good', dot: T.good },
  // Two different declines, and the difference is the whole point. `rejected`
  // is terminal; `changes_requested` comes back to be fixed.
  rejected: { label: 'Declined', tone: 'danger', dot: T.danger },
  changes_requested: { label: 'Needs a fix', tone: 'warn', dot: T.warn },
};

function SubmissionCard({ submission: s }: { submission: SubmissionRow }) {
  const look = STATUS_LOOK[s.status] ?? {
    label: s.status,
    tone: 'info' as const,
    dot: T.info,
  };
  const syncing = s.pendingSyncCount > 0;

  return (
    <div
      style={{
        padding: 14,
        background: T.surface,
        border: `1px solid ${T.stroke}`,
        borderRadius: 14,
        marginBottom: 10,
        boxShadow: T.sh,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 9 }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div
            style={{
              fontSize: 13.5,
              fontWeight: 700,
              fontFamily: T.font,
              letterSpacing: '-0.01em',
            }}
          >
            {s.title}
          </div>
          <div style={{ marginTop: 2, fontSize: 9.5, color: T.textMute, fontFamily: T.mono }}>
            {s.templateName ? `${s.templateName} · ` : ''}
            {s.captureCount} capture{s.captureCount === 1 ? '' : 's'} ·{' '}
            {s.status === 'approved' && s.approvedAt
              ? `decided ${timeAgo(s.approvedAt)}`
              : `sent ${timeAgo(s.submittedAt)}`}
          </div>
        </div>
        <Chip tone={look.tone} size="sm">
          {look.label}
        </Chip>
      </div>

      {syncing ? (
        <StatusLine
          icon={<Ico d={I.refresh} size={14} stroke={T.brand} sw={2.2} />}
          text={`${s.pendingSyncCount} change${s.pendingSyncCount === 1 ? '' : 's'} still to upload — syncs when online`}
          bg={T.surface2}
          border={T.stroke}
          color={T.textDim}
        />
      ) : s.status === 'submitted' ? (
        <StatusLine
          icon={<PulseDot color={T.info} />}
          text="Waiting on a reviewer. This updates itself when they decide."
          bg={T.surface2}
          border={T.stroke}
          color={T.textDim}
        />
      ) : s.status === 'in_review' ? (
        <StatusLine
          icon={<PulseDot color={T.warn} />}
          text={
            s.reviewerId
              ? `${personLabel(s.reviewerId)} is reading it now.`
              : 'A reviewer has it open right now.'
          }
          bg={T.warnSoft}
          border={T.warnSoft}
          color={T.warn}
        />
      ) : s.status === 'approved' ? (
        <StatusLine
          icon={<Ico d={I.check2} size={14} stroke={T.good} sw={2.4} />}
          text="Approved and locked — it's in the export now."
          bg={T.goodSoft}
          border={T.goodSoft}
          color={T.good}
        />
      ) : s.status === 'changes_requested' ? (
        <StatusLine
          icon={<Ico d={I.warn} size={14} stroke={T.warn} sw={2.2} />}
          // The note when there is one. A session that came back without a
          // reason is worth saying so plainly rather than dressing up — it
          // means the reviewer left the box empty, and the inspector needs to
          // know that is what happened rather than that we lost it.
          text={
            s.reviewNote
              ? s.reviewNote
              : 'Sent back for a fix, with no note. Ask the reviewer what they need.'
          }
          bg={T.warnSoft}
          border={T.warnSoft}
          color={T.warn}
        />
      ) : (
        <StatusLine
          icon={<Ico d={I.warn} size={14} stroke={T.danger} sw={2.2} />}
          text={
            s.reviewNote
              ? s.reviewNote
              : 'Declined at review. Open it to see what you recorded.'
          }
          bg={T.dangerSoft}
          border={T.dangerSoft}
          color={T.danger}
        />
      )}

      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 10 }}>
        <div style={{ flex: 1 }} />
        <Link
          to="/sessions/$sessionId"
          params={{ sessionId: s.id }}
          style={{
            padding: '8px 14px',
            borderRadius: 10,
            border: `1px solid ${T.stroke}`,
            background: T.surface,
            color: T.text,
            fontFamily: T.font,
            fontWeight: 600,
            fontSize: 13,
            textDecoration: 'none',
            display: 'inline-flex',
            alignItems: 'center',
            gap: 6,
          }}
        >
          <Ico d={I.folder} size={13} />
          Open timeline
        </Link>
        {/*
          Only for the status that can actually be acted on. `rejected` is
          terminal — the writer gate refuses the edit — so offering this there
          would be a button that 403s, which is the exact shape of thing this
          screen's header comment refused to ship.
        */}
        {s.status === 'changes_requested' && (
          <Link
            to="/sessions/$sessionId/form"
            params={{ sessionId: s.id }}
            style={{
              padding: '8px 14px',
              borderRadius: 10,
              border: `1px solid ${T.brand}`,
              background: T.brand,
              color: '#fff',
              fontFamily: T.font,
              fontWeight: 700,
              fontSize: 13,
              textDecoration: 'none',
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6,
            }}
          >
            <Ico d={I.edit} size={13} stroke="#fff" />
            Fix it
          </Link>
        )}
      </div>
    </div>
  );
}

function StatusLine({
  icon,
  text,
  bg,
  border,
  color,
}: {
  icon: ReactNode;
  text: string;
  bg: string;
  border: string;
  color: string;
}) {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 9,
        marginTop: 10,
        background: bg,
        border: `1px solid ${border}`,
        borderRadius: 11,
        padding: '10px 12px',
      }}
    >
      <span style={{ display: 'flex', flex: 'none' }}>{icon}</span>
      <span style={{ fontSize: 12, fontWeight: 600, color, fontFamily: T.font, lineHeight: 1.4 }}>
        {text}
      </span>
    </div>
  );
}

function PulseDot({ color }: { color: string }) {
  return (
    <span
      style={{
        width: 8,
        height: 8,
        borderRadius: 4,
        background: color,
        display: 'inline-block',
        animation: 'kap-pulse 1.6s ease-in-out infinite',
      }}
    />
  );
}

/* ---------------------------------------------------------------- empty -- */

function EmptyFeed({
  tab,
  anySubmissions,
  counts,
}: {
  tab: Tab;
  anySubmissions: boolean;
  counts: { waiting: number; approved: number; sentBack: number };
}) {
  const copy: Record<Tab, string> = {
    all: 'Sessions you submit show up here, and stay here until a reviewer decides.',
    waiting:
      counts.approved + counts.sentBack > 0
        ? 'Nothing waiting — everything you sent has been decided.'
        : 'Nothing waiting on a reviewer right now.',
    approved: 'Nothing approved yet.',
    sent_back: 'Nothing has been sent back to you.',
  };
  return (
    <div
      style={{
        padding: '40px 12px',
        textAlign: 'center',
        color: T.textDim,
        fontFamily: T.font,
        fontSize: 13,
        lineHeight: 1.5,
      }}
    >
      <div
        style={{
          width: 64,
          height: 64,
          borderRadius: 16,
          background: T.surface2,
          color: T.textDim,
          margin: '0 auto 16px',
          display: 'grid',
          placeItems: 'center',
        }}
      >
        <Ico d={I.send} size={26} sw={1.6} />
      </div>
      <div style={{ fontSize: 15, fontWeight: 600, color: T.text, marginBottom: 6 }}>
        {anySubmissions ? 'Nothing under this filter' : 'No submissions yet'}
      </div>
      {copy[tab]}
    </div>
  );
}
